import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Boxes } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useToast } from "@/components/ui/Toast";
import { usePageTitle } from "@/lib/title";
import type { Category } from "@/types";
import { Card, CardBody } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FullPageSpinner } from "@/components/ui/Spinner";
import { EmptyState } from "@/components/ui/EmptyState";

export function CategoriesPage() {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: categories, isLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: () => api.get<Category[]>("/api/v1.0/categories"),
  });

  const { mutate: createCategory, isPending } = useMutation({
    mutationFn: () => api.post("/api/v1.0/categories", { name }),
    onSuccess: () => {
      setName("");
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Failed to create category"),
  });

  const { mutate: deleteCategory } = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1.0/categories/${id}`),
    onSuccess: () => {
      notify("Category deleted");
      void queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) createCategory();
  };

  usePageTitle("Categories");

  return (
    <div className="flex flex-col gap-6">
      <p className="-mt-3 text-sm text-slate-500">Organize courses into browsable categories.</p>

      <form onSubmit={onSubmit} className="flex max-w-md gap-2">
        <Input placeholder="New category name" value={name} onChange={(e) => setName(e.target.value)} />
        <Button type="submit" loading={isPending}>
          <Plus className="h-4 w-4" /> Add
        </Button>
      </form>
      {error && <p className="text-sm text-red-600">{error}</p>}

      {isLoading ? (
        <FullPageSpinner />
      ) : !categories?.length ? (
        <EmptyState icon={Boxes} title="No categories yet" />
      ) : (
        <Card>
          <CardBody className="flex flex-col divide-y divide-slate-100">
            {categories.map((cat) => (
              <div key={cat.id} className="flex items-center justify-between py-2.5">
                <span className="text-sm text-slate-800">{cat.name}</span>
                <button onClick={() => deleteCategory(cat.id)} className="text-slate-400 hover:text-red-500">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
